import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { MdDeleteForever } from 'react-icons/md';
import { useTranslation } from 'react-i18next';
import '../../style/ManagePetImagesPage.css';

const ManagePetImagesPage = () => {
    const { id } = useParams();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [pet, setPet] = useState(null);
    const [newImages, setNewImages] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';

    useEffect(() => {
        const fetchPet = async () => {
            try {
                const storedUser = JSON.parse(localStorage.getItem('user'));
                const token = storedUser?.token;

                if (!token) {
                    throw new Error(t('error.noAuthToken'));
                }

                const response = await axios.get(`${baseUrl}/api/pets/${id}`, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                });

                setPet(response.data);
            } catch (err) {
                setError(t('error.fetchPetDetails'));
            }
        };

        fetchPet();
    }, [id, baseUrl, t]);

    const handleImageUpload = async () => {
        try {
            setLoading(true);
            const storedUser = JSON.parse(localStorage.getItem('user'));
            const token = storedUser?.token;

            if (!token) {
                throw new Error(t('error.noAuthToken'));
            }

            const formData = new FormData();
            newImages.forEach((image) => {
                formData.append('images', image);
            });

            const response = await axios.put(`${baseUrl}/api/pets/${id}/images`, formData, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'multipart/form-data',
                },
            });

            setPet(response.data);
            setNewImages([]);
            setLoading(false);

            navigate(`/pet/${id}`);
        } catch (err) {
            setError(t('error.uploadImages'));
            setLoading(false);
        }
    };

    const handleImageDelete = async (imageUrl) => {
        try {
            const storedUser = JSON.parse(localStorage.getItem('user'));
            const token = storedUser?.token;

            if (!token) {
                throw new Error(t('error.noAuthToken'));
            }

            const response = await axios.delete(`${baseUrl}/api/pets/${id}/images`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
                data: { imageUrl },
            });

            setPet(response.data);
        } catch (err) {
            setError(t('error.deleteImage'));
        }
    };

    const handleImageChange = (e) => {
        const files = Array.from(e.target.files);
        setNewImages(files);
    };

    const renderImagePreviews = () => {
        return newImages.map((image, index) => (
            <div key={index} className="image-preview-container">
                <img
                    src={URL.createObjectURL(image)}
                    alt={t('managePetImagesPage.previewAlt', { index: index + 1 })}
                />
            </div>
        ));
    };

    if (error) {
        return <div>{t('error.generalError', { error })}</div>;
    }

    if (!pet) {
        return <div>{t('loading')}</div>;
    }

    const generalImages = pet.images.filter((image) => image.tag === 'general');

    return (
        <div className="manage-pet-images-page">
            <div className="manage-pet-images-header">
                <h1>{t('managePetImagesPage.title', { name: pet.name })}</h1>
            </div>
            <div className="pet-images">
                {generalImages.map((image, index) => (
                    <div key={index} className="image-container">
                        <img
                            src={image.url}
                            alt={t('managePetImagesPage.imageAlt', { index: index + 1 })}
                        />
                        <button onClick={() => handleImageDelete(image.url)} className="delete-icon">
                            <MdDeleteForever size={24} />
                        </button>
                    </div>
                ))}
            </div>
            <div className="upload-images">
                <h3>{t('managePetImagesPage.uploadTitle')}</h3>
                <input type="file" accept="image/*" multiple onChange={handleImageChange} />
                <div className="image-previews">{renderImagePreviews()}</div>
                <button onClick={handleImageUpload} disabled={loading || newImages.length === 0}>
                    {loading ? t('managePetImagesPage.uploading') : t('managePetImagesPage.uploadButton')}
                </button>
            </div>
        </div>
    );
};

export default ManagePetImagesPage;